/**
 * Settings → Notifications (TMMAPP-0112; TMMAPI-0148, MM-D-119; flagged: an extension of v1's drawn
 * list, P 10-settings). One row per event kind — the label, a cadence control (Instant / Daily digest
 * / Off, a radio group) and the "Email" meta — then a "For each virtual account" disclosure whose rows
 * start with "Same as default". "A large allocation comes in" takes a threshold amount, required
 * unless Off. Every change is saved at once with one `PUT` of the person's rows: optimistic, rolled
 * back on an error. The defaults and each virtual account's matrix are as served (a locked owner's
 * member gets only "The owner's subscription changes"); tmm-api sends the alerts.
 *
 * "Assistant messages" (TMMAPP-0142 U3 item 21; TMMAPI-0181 U6) is a switch on the defaults, on
 * unless the person turned it off: the hourly digest of shared-session messages (`INSTANT`) or
 * `OFF`. It has no per-virtual-account row.
 */
import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import {
  Box,
  Button,
  ButtonBase,
  Collapse,
  FormControlLabel,
  Skeleton,
  Stack,
  Switch,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import {
  ALERT_CADENCES,
  ASSISTANT_MESSAGES_KIND,
  alertPreferencesApi,
  THRESHOLD_KIND,
  type AlertCadence,
  type AlertPreference,
  type AlertPreferences,
} from '@/api/alertPreferences';
import { ErrorState } from '@/components/ErrorState';
import { CollapseIcon, ExpandIcon } from '@/components/icons';
import { minorToInputText, parseMoneyToMinor } from '@/i18n/format';
import { useLocale } from '@/i18n/useLocale';
import { useMe } from '@/session/meHooks';
import { resolveTokens } from '@/theme/theme';
import type { SettingsNotify } from './settingsHref';

const SAME = 'SAME' as const;
type Choice = AlertCadence | typeof SAME;

/**
 * A cadence control: the segmented look, a real radio group (§34 record 11; RoleToggle's pattern,
 * TMMAPP-0110): one tab stop (the checked radio), and the arrow keys move and choose.
 */
function CadenceControl({
  label,
  value,
  options,
  onChange,
}: {
  label: string;
  value: Choice;
  options: readonly { value: Choice; label: string }[];
  onChange: (next: Choice) => void;
}) {
  const tokens = resolveTokens(useTheme());
  const refs = useRef<Array<HTMLButtonElement | null>>([]);

  function choose(next: Choice) {
    if (next !== value) onChange(next);
  }

  function onKeyDown(event: KeyboardEvent<HTMLButtonElement>, index: number) {
    const step =
      event.key === 'ArrowRight' || event.key === 'ArrowDown'
        ? 1
        : event.key === 'ArrowLeft' || event.key === 'ArrowUp'
          ? -1
          : 0;
    if (step === 0) return;
    event.preventDefault();
    const next = (index + step + options.length) % options.length;
    refs.current[next]?.focus();
    const option = options[next];
    if (option !== undefined) choose(option.value);
  }

  return (
    <Box
      role="radiogroup"
      aria-label={label}
      sx={{
        display: 'inline-flex',
        flexWrap: 'wrap',
        bgcolor: tokens.surface,
        border: `1px solid ${tokens.line}`,
        borderRadius: `${tokens.radius.control}px`,
        overflow: 'hidden',
      }}
    >
      {options.map((option, index) => {
        const checked = option.value === value;
        return (
          <ButtonBase
            key={option.value}
            ref={(node: HTMLButtonElement | null) => {
              refs.current[index] = node;
            }}
            role="radio"
            aria-checked={checked}
            tabIndex={checked ? 0 : -1}
            onClick={() => choose(option.value)}
            onKeyDown={(event) => onKeyDown(event, index)}
            sx={{
              p: '5px 10px',
              fontSize: 12,
              fontWeight: 500,
              lineHeight: '18px',
              whiteSpace: 'nowrap',
              color: checked ? tokens.link : tokens.text,
              bgcolor: checked ? tokens.selected : 'transparent',
              borderInlineStart: index > 0 ? `1px solid ${tokens.line}` : 'none',
              '&:hover': { bgcolor: checked ? tokens.selected : tokens.hover },
              '&.Mui-focusVisible': { outline: `2px solid ${tokens.link}`, outlineOffset: '-2px' },
            }}
          >
            {option.label}
          </ButtonBase>
        );
      })}
    </Box>
  );
}

/** The threshold field: committed on blur or Enter; required (above 0) unless the row is Off. */
function ThresholdField({
  id,
  minor,
  currency,
  error,
  onCommit,
}: {
  id: string;
  minor: number | null;
  currency: string | null;
  error: string | null;
  onCommit: (text: string) => void;
}) {
  const { t } = useTranslation();
  const locale = useLocale();
  const shown = minor === null ? '' : minorToInputText(minor, currency, locale);
  const [text, setText] = useState(shown);
  const [prevShown, setPrevShown] = useState(shown);
  if (prevShown !== shown) {
    setPrevShown(shown);
    setText(shown);
  }
  return (
    <TextField
      id={id}
      size="small"
      label={t('alertPrefs.threshold')}
      value={text}
      onChange={(event) => setText(event.target.value)}
      onBlur={() => {
        if (text !== shown) onCommit(text);
      }}
      onKeyDown={(event) => {
        if (event.key === 'Enter' && text !== shown) onCommit(text);
      }}
      error={error !== null}
      helperText={error ?? currency ?? undefined}
      sx={{ width: { xs: '100%', sm: 200 } }}
    />
  );
}

interface RowProps {
  pref: AlertPreference;
  /** The virtual account's row (with "Same as default"), or `null` for the defaults. */
  virtualAccountId: number | null;
  /** The default row this one inherits, for a virtual account's row. */
  inherited: AlertPreference | null;
  currency: string | null;
  error: string | null;
  /** A cadence picked while the threshold is missing: shown, not yet saved. */
  pending: AlertCadence | undefined;
  onCadence: (choice: Choice) => void;
  onThreshold: (text: string) => void;
}

function PreferenceRow({
  pref,
  virtualAccountId,
  inherited,
  currency,
  error,
  pending,
  onCadence,
  onThreshold,
}: RowProps) {
  const { t } = useTranslation();
  const tokens = resolveTokens(useTheme());
  const label = t(`alertPrefs.kind.${pref.eventKind}`);
  const own =
    virtualAccountId === null || pref.source === 'VIRTUAL_ACCOUNT' || pending !== undefined;
  const value: Choice = pending ?? (own ? pref.cadence : SAME);
  const options: { value: Choice; label: string }[] = [
    ...(virtualAccountId !== null ? [{ value: SAME, label: t('alertPrefs.same') }] : []),
    ...ALERT_CADENCES.map((cadence) => ({
      value: cadence,
      label: t(`alertPrefs.cadence.${cadence}`),
    })),
  ];
  const showThreshold = pref.eventKind === THRESHOLD_KIND && own;
  const fieldId = `alert-threshold-${virtualAccountId ?? 'default'}`;
  return (
    <Box
      data-alert-row={pref.eventKind}
      sx={{
        display: 'flex',
        flexDirection: { xs: 'column', md: 'row' },
        alignItems: { md: 'center' },
        gap: { xs: 1, md: 2 },
        py: 1,
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography sx={{ fontSize: 13 }}>{label}</Typography>
        {virtualAccountId !== null && !own && inherited !== null && (
          <Typography sx={{ fontSize: 12, color: tokens.textLabel('surface') }}>
            {t('alertPrefs.inherits', {
              cadence: t(`alertPrefs.cadence.${inherited.cadence}`),
            })}
          </Typography>
        )}
        {showThreshold && (
          <Box sx={{ mt: 1 }}>
            <ThresholdField
              id={fieldId}
              minor={pref.thresholdMinor}
              currency={currency}
              error={error}
              onCommit={onThreshold}
            />
          </Box>
        )}
      </Box>
      <Stack direction="row" sx={{ alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
        <CadenceControl
          label={t('alertPrefs.cadenceFor', { event: label })}
          value={value}
          options={options}
          onChange={onCadence}
        />
        <Typography
          component="span"
          sx={{ fontSize: 12, color: tokens.textLabel('surface'), minWidth: 36 }}
        >
          {t('alertPrefs.email')}
        </Typography>
      </Stack>
    </Box>
  );
}

type RowKey = string;
const rowKey = (virtualAccountId: number | null, kind: string): RowKey =>
  `${virtualAccountId ?? 'default'}|${kind}`;

export function AlertPreferencesSection({ onNotify }: { onNotify: SettingsNotify }) {
  const { t } = useTranslation();
  const locale = useLocale();
  const tokens = resolveTokens(useTheme());
  const me = useMe();
  const [prefs, setPrefs] = useState<AlertPreferences | null>(null);
  const [loadError, setLoadError] = useState<unknown>(null);
  const [tick, setTick] = useState(0);
  const [open, setOpen] = useState(false);
  const [errors, setErrors] = useState<Record<RowKey, string>>({});
  const [pendingChoice, setPendingChoice] = useState<Record<RowKey, AlertCadence>>({});
  // The last matrix the server confirmed: a failed save rolls back to it.
  const confirmed = useRef<AlertPreferences | null>(null);
  // Saves run one after another, so the server sees them in the order they were made. Each has a
  // generation: only the latest one's answer is shown, and a failure abandons the saves after it
  // (they were built on the change that failed), so nothing the person was told is unsaved persists.
  const queue = useRef<Promise<void>>(Promise.resolve());
  const generation = useRef(0);
  const abandonedThrough = useRef(0);

  useEffect(() => {
    let cancelled = false;
    void alertPreferencesApi
      .get()
      .then((next) => {
        if (cancelled) return;
        confirmed.current = next;
        setPrefs(next);
        setLoadError(null);
      })
      .catch((error: unknown) => {
        if (!cancelled) setLoadError(error ?? new Error('alert-preferences'));
      });
    return () => {
      cancelled = true;
    };
  }, [tick]);

  function save(next: AlertPreferences) {
    const mine = ++generation.current;
    setPrefs(next);
    queue.current = queue.current.then(async () => {
      if (mine <= abandonedThrough.current) return;
      try {
        const served = await alertPreferencesApi.replace(next);
        confirmed.current = served;
        if (mine === generation.current) setPrefs(served);
      } catch {
        abandonedThrough.current = generation.current;
        setPrefs(confirmed.current);
        onNotify(t('alertPrefs.saveFailed'), 'error');
      }
    });
  }

  if (prefs === null) {
    if (loadError !== null) {
      return (
        <ErrorState
          variant="section"
          error={loadError}
          what={t('errors.what.alertPreferences')}
          onRetry={() => setTick((n) => n + 1)}
        />
      );
    }
    return <Skeleton variant="rounded" height={120} />;
  }

  const currencyOf = (virtualAccountId: number | null) =>
    virtualAccountId === null
      ? me.user.currency
      : (me.virtualAccounts.find((row) => row.id === virtualAccountId)?.currency ??
        me.user.currency);

  function setError(key: RowKey, message: string | null) {
    setErrors((prev) => {
      const next = { ...prev };
      if (message === null) delete next[key];
      else next[key] = message;
      return next;
    });
  }

  function setPending(key: RowKey, choice: AlertCadence | null) {
    setPendingChoice((prev) => {
      const next = { ...prev };
      if (choice === null) delete next[key];
      else next[key] = choice;
      return next;
    });
  }

  function update(
    current: AlertPreferences,
    virtualAccountId: number | null,
    kind: AlertPreference['eventKind'],
    patch: (row: AlertPreference) => AlertPreference,
  ): AlertPreferences {
    if (virtualAccountId === null) {
      return {
        ...current,
        defaults: current.defaults.map((row) => (row.eventKind === kind ? patch(row) : row)),
      };
    }
    return {
      ...current,
      virtualAccounts: current.virtualAccounts.map((entry) =>
        entry.virtualAccountId !== virtualAccountId
          ? entry
          : {
              ...entry,
              preferences: entry.preferences.map((row) =>
                row.eventKind === kind ? patch(row) : row,
              ),
            },
      ),
    };
  }

  function changeCadence(
    virtualAccountId: number | null,
    pref: AlertPreference,
    inherited: AlertPreference | null,
    choice: Choice,
  ) {
    if (prefs === null) return;
    const key = rowKey(virtualAccountId, pref.eventKind);
    if (choice === SAME) {
      setError(key, null);
      setPending(key, null);
      save(
        update(prefs, virtualAccountId, pref.eventKind, (row) => ({
          ...row,
          cadence: inherited?.cadence ?? row.cadence,
          thresholdMinor: inherited?.thresholdMinor ?? null,
          source: 'DEFAULT',
        })),
      );
      return;
    }
    // A threshold is required unless Off: a virtual account's override starts from the default's.
    const threshold =
      pref.eventKind !== THRESHOLD_KIND
        ? null
        : pref.source === 'VIRTUAL_ACCOUNT' || virtualAccountId === null
          ? pref.thresholdMinor
          : (inherited?.thresholdMinor ?? null);
    if (pref.eventKind === THRESHOLD_KIND && choice !== 'OFF' && threshold === null) {
      // Nothing is saved until the amount is set; the field asks for it.
      setPending(key, choice);
      setError(key, t('alertPrefs.thresholdRequired'));
      requestAnimationFrame(() =>
        document.getElementById(`alert-threshold-${virtualAccountId ?? 'default'}`)?.focus(),
      );
      return;
    }
    setError(key, null);
    setPending(key, null);
    save(
      update(prefs, virtualAccountId, pref.eventKind, (row) => ({
        ...row,
        cadence: choice,
        thresholdMinor: threshold,
        source: virtualAccountId === null ? 'DEFAULT' : 'VIRTUAL_ACCOUNT',
      })),
    );
  }

  function changeThreshold(virtualAccountId: number | null, pref: AlertPreference, text: string) {
    if (prefs === null) return;
    const key = rowKey(virtualAccountId, pref.eventKind);
    const minor =
      text.trim() === '' ? null : parseMoneyToMinor(text, currencyOf(virtualAccountId), locale);
    if (minor === null || minor <= 0) {
      setError(key, t('alertPrefs.thresholdRequired'));
      return;
    }
    const pending = pendingChoice[key];
    setError(key, null);
    setPending(key, null);
    save(
      update(prefs, virtualAccountId, pref.eventKind, (row) => ({
        ...row,
        // A cadence picked while the amount was missing takes effect with it.
        cadence: pending ?? row.cadence,
        thresholdMinor: minor,
        source: virtualAccountId === null ? 'DEFAULT' : 'VIRTUAL_ACCOUNT',
      })),
    );
  }

  // Assistant messages: served on the defaults (absent reads as the system default, on).
  const assistantPref =
    prefs.defaults.find((row) => row.eventKind === ASSISTANT_MESSAGES_KIND) ?? null;
  const assistantOn = assistantPref === null || assistantPref.cadence !== 'OFF';
  function changeAssistantMessages(on: boolean) {
    if (prefs === null) return;
    const row: AlertPreference = {
      eventKind: ASSISTANT_MESSAGES_KIND,
      cadence: on ? 'INSTANT' : 'OFF',
      thresholdMinor: null,
      source: 'DEFAULT',
    };
    save({
      ...prefs,
      defaults:
        assistantPref === null
          ? [...prefs.defaults, row]
          : prefs.defaults.map((pref) => (pref.eventKind === ASSISTANT_MESSAGES_KIND ? row : pref)),
    });
  }

  const rows = (virtualAccountId: number | null, list: AlertPreference[]) =>
    list
      .filter((pref) => pref.eventKind !== ASSISTANT_MESSAGES_KIND)
      .map((pref) => {
        const inherited =
          virtualAccountId === null
            ? null
            : (prefs.defaults.find((row) => row.eventKind === pref.eventKind) ?? null);
        return (
          <PreferenceRow
            key={pref.eventKind}
            pref={pref}
            virtualAccountId={virtualAccountId}
            inherited={inherited}
            currency={currencyOf(virtualAccountId)}
            error={errors[rowKey(virtualAccountId, pref.eventKind)] ?? null}
            pending={pendingChoice[rowKey(virtualAccountId, pref.eventKind)]}
            onCadence={(choice) => changeCadence(virtualAccountId, pref, inherited, choice)}
            onThreshold={(text) => changeThreshold(virtualAccountId, pref, text)}
          />
        );
      });

  return (
    <Box>
      <Box role="group" aria-label={t('alertPrefs.defaults')}>
        {rows(null, prefs.defaults)}
        <Box data-alert-row={ASSISTANT_MESSAGES_KIND} sx={{ py: 1 }}>
          <FormControlLabel
            control={
              <Switch
                checked={assistantOn}
                onChange={(event) => changeAssistantMessages(event.target.checked)}
                slotProps={{ input: { 'aria-describedby': 'alert-assistant-messages-help' } }}
              />
            }
            label={
              <Typography sx={{ fontSize: 13 }}>
                {t('alertPrefs.kind.ASSISTANT_MESSAGES')}
              </Typography>
            }
          />
          <Typography
            id="alert-assistant-messages-help"
            sx={{ fontSize: 12, color: tokens.textLabel('surface') }}
          >
            {t('alertPrefs.assistantMessagesHelp')}
          </Typography>
        </Box>
      </Box>
      {prefs.virtualAccounts.length > 0 && (
        <Box sx={{ mt: 1 }}>
          <Button
            size="small"
            aria-expanded={open}
            aria-controls="alert-per-virtual-account"
            endIcon={open ? <CollapseIcon /> : <ExpandIcon />}
            onClick={() => setOpen((value) => !value)}
          >
            {t('alertPrefs.perVirtualAccount')}
          </Button>
          <Collapse in={open} unmountOnExit>
            <Stack id="alert-per-virtual-account" sx={{ gap: 2, mt: 1 }}>
              {prefs.virtualAccounts.map((entry) => (
                <Box
                  key={entry.virtualAccountId}
                  role="group"
                  aria-labelledby={`alert-va-${entry.virtualAccountId}`}
                  sx={{ borderTop: `1px solid ${tokens.line}`, pt: 1.5 }}
                >
                  <Typography
                    id={`alert-va-${entry.virtualAccountId}`}
                    variant="h3"
                    sx={{ fontSize: 13, fontWeight: 500 }}
                  >
                    {entry.name}
                  </Typography>
                  {rows(entry.virtualAccountId, entry.preferences)}
                </Box>
              ))}
            </Stack>
          </Collapse>
        </Box>
      )}
    </Box>
  );
}
